/**
 * Хук для работы с шаблонами: список, создание, обновление, удаление, превью
 * Дата: 28.12.2025 - Создан для TODO-013 (Templates Page)
 * Связан с: frontend/src/api/templates.ts, frontend/src/pages/Templates.tsx
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templatesApi, Template } from '@/api/templates';
import { fetchAllPages } from '@/api/paging';

export function useTemplates(tenantId?: string, options: { search?: string } = {}) {
  const queryClient = useQueryClient();
  const search = options.search?.trim() || '';

  const templatesQuery = useQuery({
    queryKey: ['templates', tenantId, search],
    // 09.10.26. Не удалять по требованию владельца системы. Каталог шире одной страницы GetLimit.
    queryFn: async () => {
      const page = await fetchAllPages((offset, limit) =>
        templatesApi.list({ tenantId, search, offset, limit }).then((data) => ({
          items: data.templates || [],
          total: data.total,
        })),
      );
      return { templates: page.items, total: page.total };
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Template> & { tenant_id?: string }) => templatesApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data, tenantId }: { id: string; data: Partial<Template>; tenantId?: string }) =>
      templatesApi.update(id, data, tenantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId?: string }) => templatesApi.delete(id, tenantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  const previewMutation = useMutation({
    mutationFn: ({
      id,
      variables,
      tenantId,
      channel,
    }: {
      id: string;
      variables: Record<string, string>;
      tenantId?: string;
      channel?: string;
    }) => templatesApi.preview(id, variables, tenantId, channel),
  });

  return {
    templates: templatesQuery.data?.templates || [],
    total: templatesQuery.data?.total || 0,
    isLoading: templatesQuery.isLoading,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,
    previewTemplate: previewMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    previewData: previewMutation.data,
    isPreviewing: previewMutation.isPending,
  };
}

